<script setup lang="ts">
interface Props {
  loading?: boolean;
  saving?: boolean;
  unauthorized?: boolean;
  notFound?: boolean;
  unknownError?: boolean;
  customError?: boolean;
  customErrorMessage?: string;
  loadingMessage?: string;
  layout?: 'center' | 'horizontal';
  solid?: boolean;
  centerTop?: boolean;
  centerTopLoad?: boolean;
}

withDefaults(defineProps<Props>(), {
  loading: false,
  saving: false,
  unauthorized: false,
  notFound: false,
  unknownError: false,
  customError: false,
  customErrorMessage: '',
  loadingMessage: '',
  layout: 'center',
  solid: false,
  centerTop: false,
  centerTopLoad: false,
});
</script>

<template>
  <div>
    <div v-if="loading">
      <div
        :class="{
          'uk-background-secondary uk-light uk-border-rounded uk-padding uk-box-shadow-medium':
            solid,
          'uk-position-center': layout === 'center' || centerTop,
          'uk-flex': layout === 'horizontal',
          'horizontal-modal-margin-extra-top': centerTopLoad,
          'horizontal-modal-margin-top': centerTop,
        }"
      >
        <div
          class="spinner-container"
          :class="{
            'uk-margin-small-right': layout === 'horizontal',
          }"
        >
          <div uk-spinner></div>
        </div>
        <div class="uk-text-center uk-text-bold" v-html="loadingMessage || 'Loading...'"></div>
      </div>
    </div>
    <div v-else-if="saving">
      <div class="uk-position-center">
        <div class="spinner-container"><div uk-spinner></div></div>
        <div>Saving...</div>
      </div>
    </div>
    <div v-else-if="unauthorized">
      <div class="uk-position-center">
        <div class="spinner-container"><span uk-icon="icon: ban; ratio: 2"></span></div>
        <div>You do not have permissions to access this resource.</div>
      </div>
    </div>
    <div v-else-if="notFound">
      <div class="uk-position-center">
        <div class="spinner-container"><span uk-icon="icon: warning; ratio: 2"></span></div>
        <div>The resource you are trying to access does not exist.</div>
      </div>
    </div>
    <div v-else-if="unknownError">
      <div class="uk-position-center">
        <div class="spinner-container"><span uk-icon="icon: warning; ratio: 2"></span></div>
        <div>An unexpected error occurred while trying to access the resource.</div>
      </div>
    </div>
    <div v-else-if="customError">
      <div class="uk-position-center">
        <div class="spinner-container"><span uk-icon="icon: warning; ratio: 2"></span></div>
        <div>{{ customErrorMessage }}</div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.spinner-container {
  text-align: center;
  margin-bottom: 0.5rem;
}

.horizontal-modal-margin-top {
  margin-top: 5px;
}

.horizontal-modal-margin-extra-top {
  margin-top: 15px;
}

// Enhanced loading states styling
.uk-background-secondary {
  .uk-text-bold {
    font-weight: 600;
    color: white;
    text-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
  }
}

// Error state styling
.uk-position-center {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;

  .spinner-container {
    margin-bottom: 1rem;
  }

  div:last-child {
    font-weight: 500;
    color: #6c757d;
  }
}
</style>
